import { useEffect, useRef } from "react";

export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const particles: { x: number; y: number; vx: number; vy: number; r: number; alpha: number }[] = [];
    for (let i = 0; i < 90; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        r: Math.random() * 2 + 0.5,
        alpha: Math.random() * 0.7 + 0.3,
      });
    }

    let animId: number;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(100, 255, 218, ${p.alpha})`;
        ctx.fill();
      });

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 130) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(100, 255, 218, ${0.15 * (1 - dist / 130)})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animId);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#071a2e]">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/19e83a285056f4aa77e1228f8ff77d81.jpg"
          alt="MDLARK hero background"
          className="w-full h-full object-cover object-top opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#071a2e]/85 via-[#071a2e]/50 to-[#071a2e]/20"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#071a2e]/30 via-transparent to-[#071a2e]/70"></div>
      </div>

      {/* Particle canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-24">
        <div className="flex flex-col lg:flex-row items-center gap-12">

          {/* Left text */}
          <div className="flex-1 text-center">
            <h1 className="mb-5">
              <span className="block text-7xl md:text-8xl lg:text-9xl font-black tracking-tight text-white leading-none drop-shadow-lg">
                MDLARK
              </span>
            </h1>

            <p className="text-white/75 text-lg md:text-xl leading-relaxed max-w-xl mx-auto mb-10">
              MDLARK operates at the frontier of three industries — sustainable food innovation with Prime Roots, bio-based industrial cleaning with Gemtek, and optimized human performance with Apollo Business Wellness.
            </p>

            <div className="flex flex-col items-center gap-5 w-full">
              <button
                onClick={() => scrollTo("food-tech")}
                className="whitespace-nowrap px-10 py-4 rounded-full bg-[#64ffda] text-[#071a2e] font-bold text-base hover:bg-[#38f5c8] transition-all duration-200 cursor-pointer shadow-lg shadow-[#64ffda]/20"
              >
                Explore Technologies
              </button>
              <div className="flex gap-4 flex-wrap justify-center">
                {[
                  { label: "Food Tech", id: "food-tech" },
                  { label: "Cleaning", id: "industrial-biotech" },
                  { label: "Wellness", id: "wellness" },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => scrollTo(tab.id)}
                    className="whitespace-nowrap px-8 py-3 rounded-full border border-[#64ffda]/50 text-[#64ffda] text-sm font-semibold hover:bg-[#64ffda]/15 transition-all duration-200 cursor-pointer tracking-wider uppercase"
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right visual — enlarged and centered */}
          <div className="w-full lg:w-[520px] flex-shrink-0">
            <div className="relative w-full h-[400px] md:h-[480px] rounded-2xl overflow-hidden border border-white/15">
              <img
                src="https://readdy.ai/api/search-image?query=futuristic%20human%20silhouette%20merging%20with%20glowing%20biotech%20circuitry%20and%20organic%20plant%20DNA%20helixes%2C%20bright%20electric%20teal%20and%20coral%20orange%20energy%20streams%20flowing%20through%20body%20outline%2C%20clean%20modern%20digital%20wellness%20visualization%20with%20vibrant%20gradients%20on%20light%20background%2C%20human%20potential%20meets%20biotechnology%20concept%20art&width=700&height=500&seq=hero_right_new_03&orientation=landscape"
                alt="Biotech innovation visualization"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#071a2e]/70 via-transparent to-transparent"></div>
            </div>
          </div>

        </div>



        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-white/40 text-xs tracking-widest uppercase">Scroll</span>
          <i className="ri-arrow-down-line text-white/40 text-lg"></i>
        </div>
      </div>
    </section>
  );
}
